import React, { useState } from 'react';
import { DataPacket_Kind } from 'livekit-client';
import { useDispatch, useSelector } from 'react-redux';
import { castVote } from '../../store/GamesSlice'; // Importez l'action
import "./Survey.css";
import { useTranslation } from 'react-i18next';

const styles = {
    container: {
        display: 'flex', flexDirection: 'column', gap: '1rem',
        width: '100%', maxWidth: '28.125rem', margin: '0 auto',
        padding: '1.25rem', background: 'white', borderRadius: '0.75rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        fontFamily: 'sans-serif'
    },
    question: {
        fontSize: '1.25rem', fontWeight: 'bold', color: '#1e293b', textAlign: 'center', marginBottom: '0.625rem'
    },
    optionContainer: {
        display: 'flex', flexDirection: 'column', gap: '0.25rem' // Espace entre bouton et votes
    },
    
    // --- STYLES MESSENGER ---
    votersContainer: {
        display: 'flex', alignItems: 'center', gap: '0.3125rem', fontSize: '0.8rem',
        paddingLeft: '0.3125rem', height: '1.25rem'
    },
    voterCountBadge: {
        background: '#e2e8f0', color: '#475569', borderRadius: '0.625rem',
        padding: '0.125rem 0.5rem', fontSize: '0.75rem', fontWeight: 'bold',
        cursor: 'help', position: 'relative' // Important pour le tooltip
    },
    // Le Tooltip caché par défaut
    tooltip: {
        position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
        marginBottom: '0.3125rem', background: '#1e293b', color: 'white',
        padding: '0.3125rem 0.625rem', borderRadius: '0.25rem', fontSize: '0.75rem',
        whiteSpace: 'nowrap', zIndex: 10, pointerEvents: 'none',
        display: 'none', // Caché
        boxShadow: '0 0.125rem 0 rgba(0,0,0,0.2)'
    },
    // Petite flèche du tooltip
    tooltipArrow: {
        position: 'absolute', top: '100%', left: '50%', marginLeft: '-0.3125rem',
        borderWidth: '0.3125rem', borderStyle: 'solid',
        borderColor: '#1e293b transparent transparent transparent'
    }
};

export default function Survey({ room, gameId, gameData, isOwner }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const { localAvatar } = useSelector(state => state.auth);
    const { question, options, isMultiple, votes = {}, isClosed } = gameData;
    const [selectedIndices, setSelectedIndices] = useState([]);
    const [hoveredOption, setHoveredOption] = useState(null); // Pour gérer l'affichage du tooltip

    // --- 1. FONCTION HELPER : Qui a voté pour l'option X ? ---
    const getVotersForOption = (optionIndex) => {
        // On transforme l'objet votes { "userId": {name, choices: []} } en tableau
        return Object.values(votes)
            .filter(vote => vote.choices && vote.choices.includes(optionIndex))
            .map(vote => vote.name);
    };

    const handleOptionClick = (index) => {
        if (isClosed) return;

        if (isMultiple) {
            setSelectedIndices(prev =>
                prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
            );
        } else {
            setSelectedIndices([index]);
        }
    };

    const handleSubmitVote = async () => {
        if (selectedIndices.length === 0) return;

        const myIdentity = room.localParticipant.identity;
        // const myName = room.localParticipant.name || "Moi"; // Si 'name' est vide dans LiveKit
        // Utilisons une valeur sûre pour le nom (vous pouvez passer localAvatar.display_name en props si besoin)
        const myName = localAvatar?.display_name;


        const payload = {
            type: 'VOTE_CAST',
            gameId: gameId,
            roomId: room.name,
            voterId: myIdentity,
            voterName: myName,
            selectedOptions: selectedIndices
        };

        // 1. Mise à jour Locale (Immédiate)
        dispatch(castVote(payload));

        // 2. Envoi Réseau
        const encoder = new TextEncoder();
        const data = encoder.encode(JSON.stringify(payload));

        try {
            await room.localParticipant.publishData(data, {
                reliability: DataPacket_Kind.RELIABLE,
                destinationIdentities: []
            });
        } catch (e) { console.error("Erreur vote:", e); }
    };

    return (
        <div className='game-body-survey'>
            <div className='survey-form-container'>
                <div style={styles.question}>{question}</div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {options.map((opt, index) => {
                        const isSelected = selectedIndices.includes(index);
                        const voters = getVotersForOption(index); // Liste des noms ["Ali", "Sarah"]
                        const hasVotes = voters.length > 0;
                        const isHovering = hoveredOption === index;

                        return (
                            <div key={index} style={styles.optionContainer}>
                                {/* BOUTON D'OPTION */}
                                <button
                                    className={`response-choice ${isSelected ? 'response-choice-active' : ''}`}
                                    onClick={() => handleOptionClick(index)}
                                    disabled={isClosed}
                                >
                                    <span>{opt}</span>
                                    <span style={{ fontSize: '1.2rem' }}>
                                        {
                                            isMultiple ?
                                                <>
                                                    {isSelected ?
                                                        <span class="material-symbols-outlined">
                                                            select_check_box
                                                        </span>
                                                        :
                                                        <span class="material-symbols-outlined">
                                                            check_box_outline_blank
                                                        </span>
                                                    }
                                                </>
                                                :
                                                <>
                                                    {isSelected ?
                                                        <span class="material-symbols-outlined">
                                                            task_alt
                                                        </span>
                                                        :
                                                        <span class="material-symbols-outlined">
                                                            radio_button_unchecked
                                                        </span>
                                                    }
                                                </>
                                        }
                                    </span>
                                </button>

                                {/* ZONE DES VOTES (Style Messenger) */}
                                <div style={styles.votersContainer}>
                                    {hasVotes && (
                                        <div
                                            style={styles.voterCountBadge}
                                            onMouseEnter={() => setHoveredOption(index)}
                                            onMouseLeave={() => setHoveredOption(null)}
                                        >
                                            {/* Icône petit bonhomme + Nombre */}
                                            <span style={{ marginRight: '0.25rem' }}>👤</span>
                                            {voters.length}

                                            {/* TOOLTIP (Affiché si hover) */}
                                            <div style={{
                                                ...styles.tooltip,
                                                display: isHovering ? 'block' : 'none'
                                            }}>
                                                {/* On affiche les noms séparés par des virgules */}
                                                {voters.join(', ')}
                                                <div style={styles.tooltipArrow}></div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Si personne n'a voté, on laisse vide ou on met un petit placeholder */}
                                    {!hasVotes && 
                                    <span style={{ color: '#cbd5e1', fontSize: '0.7rem' }}>
                                        {t("no_votes")}
                                    </span>}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* FOOTER */}
                <div style={{ marginTop: '0.9375rem', textAlign: 'center' }}>
                    {!isClosed && (
                        <button
                            className='create-btn'
                            onClick={handleSubmitVote}
                            disabled={selectedIndices.length === 0}
                        >
                            {t("send_vote")}
                        </button>
                    )}
                    <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#64748b' }}>
                        {isMultiple ? t("multiple_choice") : t("single_choice")}
                    </div>
                </div>
            </div>
        </div>
    );
}